/* eslint-disable react-hooks/exhaustive-deps */
import { gql, useLazyQuery } from '@apollo/client';
import { Badge, Box, Button, Divider, Flex, SimpleGrid, Tab, TabList, TabPanel, TabPanels, Tabs, Text, useDisclosure, useToast } from '@chakra-ui/react';
import { faFileAlt, faUserEdit } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import moment from 'moment';
import { forwardRef, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import UserDislayNameById from '../../../../components/UserDislayNameById';
import c from '../../../../constant';
import SpreadSheetTableMtpl from '../../../../containers/MarketDevelopment/Components/mtpl-table';
import SpreadSheetTableNatCat from '../../../../containers/MarketDevelopment/Components/nat-cat-table';
import useMoment from '../../../../hooks/useMoment';
import useStore from '../../../../store';
import { isNullOrBlank } from '../../../../utils/checker';
import { toFormatCurrency } from '../../../../utils/format';
import { isAbleAllowEditing, isConViewer, isDataViewer } from '../../../../utils/market-development';
import { mdMonthlyDueDate } from '../../../../utils/mdReportDueDate';
import CompanyName from '../../../CompanyName';
import LabelDate from '../../../LabelDate';
import Loading from '../../../Loading';
import EditMonthlyReportModal from '../../../Modal/EditMonthlyReportModal';
import { CompanyBadge } from '../../../Other';
import ContributionSale from '../../Miscellaneous/contribution-sale';
import EditHistoryModal from '../../Miscellaneous/edit-history-modal';
import SpreadSheetKeyValuePair from '../../Miscellaneous/spreadsheet-key-value-pair';
import ExcelGeneral from '../Excel/ExcelGeneral';
import SpreadSheetTable from './table';

const Q_DETAIL = gql`
	query get($id: String!) {
		getMonthlySaleReportGeneralById(id: $id) {
			id
			reportType
			month
			year
			createdAt
			updatedAt
			company {
				id
				name
				nameKhmer
				logo
				type
			}
			isEditing
			editingUpdatedAt
			editingUpdatedBy
			editingDueDate
			editingDescription
			penalties {
				id
				active
			}
			contribution {
				id
				rate
				grossPremium
				attachmentFile
			}
			saleItems {
				id
				saleReportType
				saleProducts {
					productLine {
						id
						nameKh
						nameEn
						type
						order
					}
					productType {
						id
						nameKh
						nameEn
					}
					businessType
					policyType
					productValues {
						id
						saleProductId
						value
					}
				}
			}
			mtplItems {
				id
				reportId
				template {
					id
					titleKhmer
					titleEnglish
					code
				}
				labels {
					id
					templateLabel {
						id
						titleKhmer
						titleEnglish
						code
					}
					values {
						id
						value
						province {
							id
							name
							latinName
							code
						}
					}
				}
			}
		}
	}
`;

const Q_TEMPLATES = gql`
	query get($code: String!) {
		getTemplateByCode(code: $code) {
			id
			titleKhmer
			titleEnglish
			code
			order
			labels {
				id
				code
				titleKhmer
				titleEnglish
				level
				order
				readOnly
			}
			provinces {
				id
				code
				name
				latinName
			}
		}
	}
`;

// natcat
const Q_NATCAT = gql`
	query get($id: String!) {
		getMonthlySaleNatCatReportByReportId(id: $id) {
			id
			reportId
			itemProvinces {
				id
				itemId
				province {
					id
					name
					latinName
					code
				}
				values {
					id
					itemProvinceId
					valueType
					subValueType
					value
				}
			}
		}
	}
`;

const Page = forwardRef(({ id = null }, ref) => {
	const toast = useToast();
	const { t } = useTranslation();
	const { currentLang: lang } = useStore((state) => state.currentLang);
	const { isOpen, onOpen, onClose } = useDisclosure();
	const { toDateString, toMonth, toYear } = useMoment();
	const { currentUser: user } = useStore((state) => state?.currentUser);
	const [month, setMonth] = useState(null);
	const [year, setYear] = useState(null);
	const [isEditing, setEditing] = useState(false);
	const [createdAt, setCreatedAt] = useState(null);
	const [updatedAt, setUpdatedAt] = useState(null);
	const [isPenalized, setPenalized] = useState(false);
	const [company, setCompany] = useState(null);
	const [saleItems, setSaleItems] = useState(null);
	const [object, setObject] = useState(null);
	const [objectNatCat, setObjectNatCat] = useState(null);
	const { isOpen: isHistoryOpen, onOpen: onHistoryOpen, onClose: onHistoryClose } = useDisclosure();
	const [getDetail, { data: dataGet, loading: loadingGet, error: errorGet }] = useLazyQuery(Q_DETAIL, { fetchPolicy: 'no-cache' });
	const [getNatCat, { data: dataNatCat }] = useLazyQuery(Q_NATCAT);
	const [getTemplates, { data: dataTemplate, error: errorTemplate }] = useLazyQuery(Q_TEMPLATES);

	useEffect(loadDetail, [id]);

	useEffect(loadNatCat, [id]);

	useEffect(loadTemplates, [dataGet]);

	useEffect(() => {
		if (dataGet?.getMonthlySaleReportGeneralById) {
			setMonth(parseInt(dataGet?.getMonthlySaleReportGeneralById?.month));
			setYear(parseInt(dataGet?.getMonthlySaleReportGeneralById?.year));
			setEditing(dataGet?.getMonthlySaleReportGeneralById?.isEditing);
			setCreatedAt(dataGet?.getMonthlySaleReportGeneralById?.createdAt);
			setUpdatedAt(dataGet?.getMonthlySaleReportGeneralById?.updatedAt);
			setPenalized(dataGet?.getMonthlySaleReportGeneralById?.penalties?.length > 0);
			setCompany(dataGet?.getMonthlySaleReportGeneralById?.company);
			setSaleItems(dataGet?.getMonthlySaleReportGeneralById?.saleItems);
		}
	}, [dataGet]);

	// natcat
	useEffect(() => {
		if (dataNatCat?.getMonthlySaleNatCatReportByReportId) {
			const itemProvinces = dataNatCat?.getMonthlySaleNatCatReportByReportId?.itemProvinces;
			const provinces = itemProvinces?.map((itemProvince) => itemProvince?.province);
			const cells = itemProvinces?.map((itemProvince) => itemProvince?.values?.map((value) => ({ ...value, readOnly: true })));
			setObjectNatCat({ provinces, cells });
		}
	}, [dataNatCat]);

	// mtpl
	useEffect(() => {
		if (dataTemplate?.getTemplateByCode && dataGet?.getMonthlySaleReportGeneralById?.mtplItems?.length > 0) {
			const object = dataTemplate?.getTemplateByCode;
			const labels = object?.labels;
			const provinces = object?.provinces?.filter((el) => el?.code !== '9999').sort((a, b) => a?.code < b?.code);
			setObject({
				...object,
				cells: labels?.map((label, i) => {
					if (label?.readOnly) {
						return provinces?.map((el) => ({ value: '', readOnly: true }));
					} else {
						const localTemplate = dataGet?.getMonthlySaleReportGeneralById?.mtplItems?.find((el) => el?.template?.code === c.mtplGSale);
						const localLabels = localTemplate?.labels;
						const localLabel = localLabels?.find((el) => el?.templateLabel?.code === label?.code);
						const localValues = localLabel?.values?.sort((a, b) => a?.province?.code < b?.province?.code);
						return localValues?.map((el) => ({
							value: (() => {
								if (false === isNullOrBlank(el?.value)) {
									return toFormatCurrency(el?.value, null);
								}
								return el?.value;
							})(),
							readOnly: true,
						}));
					}
				}),
				provinces,
			});
		}
	}, [dataTemplate]);

	useEffect(() => {
		if (errorGet) {
			if (errorGet?.graphQLErrors?.length > 0) {
				errorGet?.graphQLErrors.map(({ message }, i) =>
					toast({
						title: t(message),
						status: 'error',
						isClosable: true,
						position: 'bottom-right',
					}),
				);
			} else {
				toast({
					title: t(errorGet?.message ? errorGet?.message : 'something went wrong'),
					status: 'error',
					isClosable: true,
					position: 'bottom-right',
				});
			}
		}
	}, [errorGet]);

	// mtpl
	useEffect(() => {
		if (errorTemplate) {
			if (errorTemplate?.graphQLErrors?.length > 0) {
				errorTemplate?.graphQLErrors.map(({ message }, i) =>
					toast({
						title: t(message),
						status: 'error',
						isClosable: true,
						position: 'bottom-right',
					}),
				);
			} else {
				toast({
					title: t(errorTemplate?.message ? errorTemplate?.message : 'something went wrong'),
					status: 'error',
					isClosable: true,
					position: 'bottom-right',
				});
			}
		}
	}, [errorTemplate]);

	function loadDetail() {
		id && getDetail({ variables: { id } });
	}

	// natcat
	function loadNatCat() {
		id && getNatCat({ variables: { id } });
	}

	// mtpl
	function loadTemplates() {
		dataGet?.getMonthlySaleReportGeneralById && getTemplates({ variables: { code: c.mtplGSale } });
	}

	const dueDate = useMemo(() => mdMonthlyDueDate({ year, month }), [year, month]);
	const isDueDateReached = useMemo(() => (dueDate ? moment().isSameOrAfter(dueDate, 'second') : false), [dueDate]);
	const canViewData = useMemo(() => isDataViewer({ user, companyType: 'General' }), [user]);
	const canViewCon = useMemo(() => isConViewer({ user, companyType: 'General' }), [user]);
	const canAllowEditing = useMemo(() => canViewData && isAbleAllowEditing({ user, companyType: 'General' }), [user]);

	return (
		<>
			{loadingGet ? (
				<>
					<Loading />
				</>
			) : (
				<>
					<Flex pl={4} pr={4} pt={0}>
						<SimpleGrid columns={[1, null, 2]} w="100%" spacingX={4} spacingY={4}>
							<Flex flex={1} direction="column" border="1px dotted #dbdbdb" borderRadius={c.borderRadius} pl={4} pr={4} pt={4} pb={4}>
								<>
									{isPenalized && (
										<Box>
											<Badge colorScheme="red" variant="solid">
												{t('Penalized')}
											</Badge>
										</Box>
									)}
								</>
								<SpreadSheetKeyValuePair label="Company" value={<CompanyName company={company} />} />
								<SpreadSheetKeyValuePair label="Type" value={<CompanyBadge type={company?.type} />} />
								<SpreadSheetKeyValuePair label="Monthly" value={toMonth(month)} />
								<SpreadSheetKeyValuePair label="Year" value={toYear(year)} />
								<SpreadSheetKeyValuePair label="Submitted At" value={<LabelDate input={createdAt} outputFormat={c.dateTimeFormat} />} />
								<>
									{updatedAt && (
										<>
											<SpreadSheetKeyValuePair
												label="Updated at"
												value={<LabelDate input={updatedAt} outputFormat={c.dateTimeFormat} />}
											/>
										</>
									)}
								</>
								{isDueDateReached && canViewData && isEditing && (
									<>
										<Divider variant="dashed" mt={2} mb={2} />
										<SpreadSheetKeyValuePair
											label="Allow Editing"
											value={
												<LabelDate input={dataGet?.getMonthlySaleReportGeneralById?.editingUpdatedAt} outputFormat={c.dateTimeFormat} />
											}
										/>
										<SpreadSheetKeyValuePair
											label="By"
											value={<UserDislayNameById id={dataGet?.getMonthlySaleReportGeneralById?.editingUpdatedBy} />}
										/>
										<SpreadSheetKeyValuePair
											label="Due Date"
											value={
												<LabelDate input={dataGet?.getMonthlySaleReportGeneralById?.editingDueDate} outputFormat={c.dateTimeFormat} />
											}
										/>
										<SpreadSheetKeyValuePair label="Description" value={dataGet?.getMonthlySaleReportGeneralById?.editingDescription} />
										{canAllowEditing && (
											<>
												<Flex mt={2}>
													<Button
														onMouseDown={onOpen}
														isLoading={false}
														isDisabled={false}
														onClick={() => null}
														leftIcon={<FontAwesomeIcon icon={faUserEdit} style={{ fontSize: 16 }} />}
														colorScheme="red"
														variant="solid"
														size="sm"
														borderRadius={c.borderRadius}
														border="1px solid #bdc3c7"
														mr={2}
														width={lang === 'kh' ? '219px' : '195px'}
													>
														{t('Stop Allowing Editing')}
													</Button>
												</Flex>
											</>
										)}
									</>
								)}
								<Flex mt={2}>
									{isDueDateReached && canAllowEditing && !isEditing && (
										<Button
											onMouseDown={onOpen}
											isLoading={false}
											isDisabled={false}
											onClick={() => null}
											leftIcon={<FontAwesomeIcon icon={faUserEdit} style={{ fontSize: 16 }} />}
											colorScheme="gray"
											variant="solid"
											size="sm"
											borderRadius={c.borderRadius}
											border="1px solid #bdc3c7"
											mr={2}
										>
											{t('Allow Editing')}
										</Button>
									)}
									{saleItems && canViewData && (
										<ExcelGeneral
											month={month}
											year={year}
											numColumns={16}
											saleItems={saleItems}
											mtplItems={object}
											natCatItems={objectNatCat}
										/>
									)}
									{canViewData && (
										<Flex ml={2}>
											<Button
												colorScheme="gray"
												variant="solid"
												size="sm"
												borderRadius={c.borderRadius}
												border="1px solid #bdc3c7"
												leftIcon={<FontAwesomeIcon icon={faFileAlt} style={{ fontSize: 16 }} />}
												onClick={onHistoryOpen}
											>
												{t('View Logs')}
											</Button>
										</Flex>
									)}
								</Flex>
								{!isDueDateReached && canViewData && dueDate && (
									<Box h="100%" mt={2}>
										<Text fontSize="15px" fontStyle="italic" fontWeight="bold" justifyContent="center" color="orange.500">
											{t('Report can still be edited until the due date:')}&nbsp;
											{toDateString(dueDate, c.dateTimeFormat)}
										</Text>
									</Box>
								)}
							</Flex>
							<Flex hidden={canViewCon ? false : true}>
								<ContributionSale contribution={dataGet?.getMonthlySaleReportGeneralById?.contribution} />
							</Flex>
						</SimpleGrid>
					</Flex>
					<Box pl={4} pr={4} pb={4} pt={4} overflow="clip" hidden={canViewData ? false : true}>
						<Tabs>
							<TabList borderBottomWidth="1px" borderBottomStyle="solid" borderBottomColor="gray.200">
								{saleItems?.map((el) => {
									return (
										<Tab key={el?.id}>
											<Text>{t(el?.saleReportType)}</Text>
										</Tab>
									);
								})}
								{object && (
									<Tab>
										<Text>{t('MTPL')}</Text>
									</Tab>
								)}
								{objectNatCat && (
									<Tab>
										<Text>{t('Natural Catastrophe')}</Text>
									</Tab>
								)}
							</TabList>
							<TabPanels>
								{saleItems?.map((el) => {
									return (
										<TabPanel key={el?.id} pl={0} pr={0} pb={0}>
											<SpreadSheetTable saleProducts={el?.saleProducts} />
										</TabPanel>
									);
								})}
								{object && (
									<TabPanel pl={0} pr={0} pb={0}>
										<SpreadSheetTableMtpl
											readOnlys={[0, 1, 6, 11, 16]}
											labels={object?.labels}
											provinces={object?.provinces}
											cells={object?.cells}
											onChange={() => null}
										/>
									</TabPanel>
								)}
								{objectNatCat && (
									<TabPanel pl={0} pr={0} pb={0}>
										<SpreadSheetTableNatCat provinces={objectNatCat?.provinces} cells={objectNatCat?.cells} onChange={() => null} />
									</TabPanel>
								)}
							</TabPanels>
						</Tabs>
					</Box>
				</>
			)}
			{isOpen && <EditMonthlyReportModal id={id} type="Sale" isEditable={isEditing} onClose={onClose} onCallback={loadDetail} />}
			{isHistoryOpen && <EditHistoryModal id={id} period="Monthly" category="Sale" isOpen={isHistoryOpen} onClose={onHistoryClose} />}
		</>
	);
});

export default Page;
